/**
 * Color Utilities Module
 * Provides color conversion and validation functions
 */

import type { HSL, RGB } from '../types';

export class ColorUtils {
    /**
     * Convert RGB string to hex
     * @param rgb - RGB color string like "rgb(255, 0, 0)"
     * @returns Hex color string or the original if already hex/other format
     */
    static rgb2hex(rgb: string): string {
        if (!rgb) return '#000000';
        
        const match = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (!match) return rgb; // Already hex or other format
        
        const r = parseInt(match[1]).toString(16).padStart(2, '0');
        const g = parseInt(match[2]).toString(16).padStart(2, '0');
        const b = parseInt(match[3]).toString(16).padStart(2, '0');
        
        return `#${r}${g}${b}`;
    }

    /**
     * Convert hex color to HSL
     * @param hex - Hex color string like "#ff0000"
     * @returns HSL object with h, s, l properties
     */
    static hexToHsl(hex: string): HSL {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h: number, s: number;
        const l = (max + min) / 2;
        
        if (max === min) {
            h = s = 0; // achromatic
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            
            switch (max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
                default: h = 0;
            }
            h /= 6;
        }
        
        return {
            h: Math.round(h * 360),
            s: Math.round(s * 100),
            l: Math.round(l * 100)
        };
    }
    
    /**
     * Convert HSL to RGB
     * @param h - Hue (0-360)
     * @param s - Saturation (0-100)
     * @param l - Lightness (0-100)
     * @returns RGB object with r, g, b properties
     */
    static hslToRgb(h: number, s: number, l: number): RGB {
        h /= 360;
        s /= 100;
        l /= 100;
        
        const hue2rgb = (p: number, q: number, t: number): number => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1/6) return p + (q - p) * 6 * t;
            if (t < 1/2) return q;
            if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
            return p;
        };
        
        let r: number, g: number, b: number;
        
        if (s === 0) {
            r = g = b = l; // achromatic
        } else {
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1/3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1/3);
        }
        
        return {
            r: Math.round(r * 255),
            g: Math.round(g * 255),
            b: Math.round(b * 255)
        };
    }
    
    /**
     * Convert HSL to hex
     * @param h - Hue (0-360)
     * @param s - Saturation (0-100)
     * @param l - Lightness (0-100)
     * @returns Hex color string
     */
    static hslToHex(h: number, s: number, l: number): string {
        const rgb = this.hslToRgb(h, s, l);
        const toHex = (c: number): string => {
            const hex = c.toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };
        return '#' + toHex(rgb.r) + toHex(rgb.g) + toHex(rgb.b);
    }

    /**
     * Validate if a string is a valid color
     * @param color - Color string to validate
     * @returns True if valid color
     */
    static isValidColor(color: string): boolean {
        if (!color) return false;
        const s = new Option().style;
        s.color = color;
        return s.color !== '';
    }

    /**
     * Get a predefined color palette
     * @returns Array of hex color strings
     */
    static getDefaultPalette(): string[] {
        return [
            '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
            '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
        ];
    }
}
